import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Storage } from '@ionic/storage';
import * as Sentry from '@sentry/browser';

const NOTIFICATION_TOKEN_KEY = 'push_notification_token';
const NOTIFICATION_PERMISSION_KEY = 'push_notification_permission';
const NOTIFICATION_DENIED_AT_KEY = 'push_notification_denied_at';
const NOTIFICATION_PROMPT_DISMISSED_AT_KEY = 'push_notification_prompt_dismissed_at';
const LAST_NOTIFICATION_ACTION_KEY = 'push_notification_last_action';

/**
 * Full-featured notification helper:
 * - Initializes push permissions/registration when supported
 * - Persists permission + token in Ionic Storage
 * - Exposes hooks so components can route registration to Redux/middleware
 *   and handle notification events.
 */
class NotificationService {
  constructor() {
    this.isInitialized = false;
    this.listenersRegistered = false;
    this.token = null;
    this.storage = new Storage();
    this.storage.create();
  }

  isPushSupported() {
    const platform = Capacitor.getPlatform();
    return platform !== 'web' && platform !== 'ios';
  }

  setupListeners(callbacks = {}) {
    if (!this.isPushSupported() || this.listenersRegistered) {
      return;
    }

    this.listenersRegistered = true;

    PushNotifications.addListener('registration', async (token) => {
      this.token = token.value;
      await this.storage.set(NOTIFICATION_TOKEN_KEY, token.value);
      if (callbacks.onRegistration) {
        callbacks.onRegistration(token.value);
      }
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error);
      if (callbacks.onRegistrationError) {
        callbacks.onRegistrationError(error);
      }
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (callbacks.onNotificationReceived) {
        callbacks.onNotificationReceived(notification);
      }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
      if (callbacks.onNotificationAction) {
        await callbacks.onNotificationAction(action);
      }
    });

    App.addListener('appStateChange', async (state) => {
      if (state.isActive && callbacks.onAppStateChange) {
        await callbacks.onAppStateChange(state);
      }
    });
  }

  reset(clearStored = false) {
    this.isInitialized = false;
    this.listenersRegistered = false;
    this.token = null;
    if (clearStored) {
      this.storage.remove(NOTIFICATION_TOKEN_KEY);
      this.storage.remove(NOTIFICATION_PERMISSION_KEY);
      this.storage.remove(NOTIFICATION_DENIED_AT_KEY);
      this.storage.remove(NOTIFICATION_PROMPT_DISMISSED_AT_KEY);
      this.storage.remove(LAST_NOTIFICATION_ACTION_KEY);
    }
  }

  async getStoredToken() {
    try {
      return await this.storage.get(NOTIFICATION_TOKEN_KEY);
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'getStoredToken' } });
      return null;
    }
  }

  async hasPermission() {
    try {
      const value = await this.storage.get(NOTIFICATION_PERMISSION_KEY);
      return value === 'granted';
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'hasPermission' } });
      return false;
    }
  }

  async syncPermissionStatus() {
    try {
      const permissionStatus = await PushNotifications.checkPermissions();
      if (permissionStatus.receive === 'granted') {
        await this.storage.set(NOTIFICATION_PERMISSION_KEY, 'granted');
        await this.storage.remove(NOTIFICATION_DENIED_AT_KEY);
      } else if (permissionStatus.receive === 'denied') {
        await this.storage.set(NOTIFICATION_PERMISSION_KEY, 'denied');
        await this.storage.set(NOTIFICATION_DENIED_AT_KEY, Date.now());
      }
      return permissionStatus.receive;
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'syncPermissionStatus' } });
      return null;
    }
  }

  // Used after the user explicitly confirms in our prompt; skips pre-checks already done upstream.
  async requestPermissionAfterPrompt() {
    try {
      const permissionStatus = await PushNotifications.requestPermissions();
      if (permissionStatus.receive === 'granted') {
        await this.storage.set(NOTIFICATION_PERMISSION_KEY, 'granted');
        await this.storage.remove(NOTIFICATION_DENIED_AT_KEY);
        await PushNotifications.register();
        this.isInitialized = true;
        return { success: true, token: this.token, error: null };
      }
      await this.storage.set(NOTIFICATION_PERMISSION_KEY, 'denied');
      await this.storage.set(NOTIFICATION_DENIED_AT_KEY, Date.now());
      return { success: false, token: null, error: 'Push notification permission denied' };
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'initialize_after_prompt' } });
      return { success: false, token: null, error: error.message };
    }
  }

  async shouldThrottlePermissionRequest() {
    try {
      const deniedAt = await this.storage.get(NOTIFICATION_DENIED_AT_KEY);
      if (!deniedAt) {
        return false;
      }
      const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
      return Date.now() - deniedAt < SEVEN_DAYS_MS;
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'throttlePermission' } });
      return false;
    }
  }

  async canShowPrompt() {
    if (!this.isPushSupported()) {
      return false;
    }
    const permissionStatus = await this.syncPermissionStatus();
    if (permissionStatus === 'granted') {
      return false;
    }
    if (await this.hasPermission()) {
      return false;
    }
    if (await this.shouldThrottlePermissionRequest()) {
      return false;
    }
    if (await this.wasPromptDismissedRecently()) {
      return false;
    }
    return true;
  }

  async recordPromptDismissed() {
    try {
      await this.storage.set(NOTIFICATION_PROMPT_DISMISSED_AT_KEY, Date.now());
    } catch (error) {
      console.error('Error recording prompt dismissed:', error);
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'recordPromptDismissed' } });
    }
  }

  async wasPromptDismissedRecently() {
    try {
      const dismissedAt = await this.storage.get(NOTIFICATION_PROMPT_DISMISSED_AT_KEY);
      if (!dismissedAt) {
        return false;
      }
      const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
      return Date.now() - dismissedAt < SEVEN_DAYS_MS;
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'wasPromptDismissedRecently' } });
      return false;
    }
  }

  async checkPendingNotifications() {
    // Placeholder: fetch missed notifications when app returns to foreground.
    console.log('Checking for pending notifications...');
  }

  async storeLastAction(actionPayload) {
    try {
      await this.storage.set(LAST_NOTIFICATION_ACTION_KEY, actionPayload);
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'storeLastAction' } });
    }
  }

  async getStoredLastAction() {
    try {
      return await this.storage.get(LAST_NOTIFICATION_ACTION_KEY);
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'getStoredLastAction' } });
      return null;
    }
  }

  async clearStoredLastAction() {
    try {
      await this.storage.remove(LAST_NOTIFICATION_ACTION_KEY);
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'clearStoredLastAction' } });
    }
  }

  async clearDeliveredNotifications(notification = null) {
    try {
      if (notification && notification.id !== undefined && notification.id !== null) {
        await PushNotifications.removeDeliveredNotifications({
          notifications: [{ id: notification.id }]
        });
        return;
      }
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'notifications', action: 'clearDeliveredNotifications' } });
    }
  }

  getToken() {
    return this.token;
  }
}

const notificationService = new NotificationService();
export default notificationService;

